import { useState } from 'react';
import { X, Video, Zap, Crown, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * VideoCreditsModal - Contextual alert for insufficient video credits
 * Shows personalized options based on what the user tried to do
 */
export default function VideoCreditsModal({ 
  isOpen, 
  onClose, 
  creditsNeeded, 
  creditsAvailable,
  videoType, // "10s_720p", "20s_720p", "20s_1080p", "30s_1080p"
  options, // { primary, secondary } from API
  onAction,
  userPlan
}) {
  const [showAllOptions, setShowAllOptions] = useState(false);
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  // Parse video type for display
  const getVideoTypeLabel = (type) => {
    const labels = {
      "10s_720p": "Quick Clip (10s 720p)",
      "20s_720p": "Standard (20s 720p)",
      "20s_1080p": "HD Standard (20s 1080p)",
      "30s_1080p": "HD Extended (30s 1080p)"
    };
    return labels[type] || type;
  };
  
  // Handle action clicks
  const handleAction = (action) => {
    switch (action) {
      case 'upgrade_to_pro':
        navigate('/pricing?highlight=pro');
        onClose();
        break;
      case 'upgrade_plan':
        navigate('/pricing');
        onClose();
        break;
      case 'buy_hd_pack':
        onAction?.('buy', 'video_hd');
        break;
      case 'buy_standard_pack':
        onAction?.('buy', 'video_standard');
        break;
      case 'buy_quick_pack':
        onAction?.('buy', 'video_quick');
        break;
      case 'switch_to_720p':
        onAction?.('switch', '20s_720p');
        onClose();
        break;
      case 'switch_to_10s':
        onAction?.('switch', '10s_720p');
        onClose();
        break;
      default:
        onClose();
    }
  };
  
  // Get icon for action type
  const getActionIcon = (type) => {
    switch (type) {
      case 'upgrade': return <Crown className="w-5 h-5" />;
      case 'payg': return <Zap className="w-5 h-5" />;
      case 'downgrade': return <Video className="w-5 h-5" />;
      default: return <Zap className="w-5 h-5" />;
    }
  };
  
  // Determine title based on situation
  const getTitle = () => {
    if (options?.primary?.type === 'upgrade' && options?.primary?.action === 'upgrade_to_pro') {
      return 'HD Videos Require Pro Plan';
    }
    return 'Insufficient Video Credits';
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-viraly-card border border-white/10 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-400" />
            </div>
            <h3 className="font-heading text-xl font-bold">{getTitle()}</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Credits info */}
        <div className="bg-white/5 rounded-xl p-4 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-viraly-muted">You wanted:</span>
            <span className="font-medium">{getVideoTypeLabel(videoType)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-viraly-muted">Credits needed:</span>
            <span className="font-bold text-red-400">{creditsNeeded}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-viraly-muted">You have:</span>
            <span className="font-bold text-viraly-orange">{creditsAvailable}</span>
          </div>
        </div>
        
        {/* Primary action */}
        {options?.primary && (
          <Button
            onClick={() => handleAction(options.primary.action)}
            className={`w-full mb-3 ${
              options.primary.type === 'upgrade' 
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' 
                : 'btn-primary'
            }`}
            data-testid="video-credits-primary-btn"
          >
            <div className="flex items-center justify-center gap-2">
              {getActionIcon(options.primary.type)}
              <span>{options.primary.label}</span>
            </div>
            {options.primary.sublabel && (
              <span className="text-xs opacity-80 ml-2">{options.primary.sublabel}</span>
            )}
          </Button>
        )}
        
        {/* See all options toggle */}
        <button
          onClick={() => setShowAllOptions(!showAllOptions)}
          className="w-full flex items-center justify-center gap-2 text-viraly-muted hover:text-white py-2 transition-colors"
          data-testid="video-credits-expand-btn"
        >
          {showAllOptions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="text-sm">{showAllOptions ? 'Hide options' : 'See all options'}</span>
        </button>
        
        {/* Secondary options */}
        {showAllOptions && options?.secondary && (
          <div className="mt-3 space-y-2 border-t border-white/10 pt-3">
            {options.secondary.map((opt, i) => (
              <button
                key={i}
                onClick={() => handleAction(opt.action)}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-left"
                data-testid={`video-credits-option-${i}`}
              >
                <div className="flex items-center gap-2">
                  {getActionIcon(opt.type)}
                  <span>{opt.label}</span>
                </div>
                {opt.sublabel && (
                  <span className="text-xs text-viraly-muted">{opt.sublabel}</span>
                )}
              </button>
            ))}
          </div>
        )}
        
        {/* Info message */}
        {options?.primary?.message && (
          <p className="text-center text-sm text-viraly-muted mt-4">
            {options.primary.message}
          </p>
        )}
      </div>
    </div>
  );
}
